import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { WebhookTypeBadge } from '@/components/pages/Webhooks/components/WebhookTypeBadge';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { type ConnectedDialog } from '@/hooks/useDialogs';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { Webhook } from '@/shared/types/webhook';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import { ActionsCell } from './ActionsCell';
import { AssignedToCell } from './AssignedToCell';
import { WebhookStatusDot } from './WebhookStatusDot';

interface ColumnsProps {
  brainId: string;
  slug: string;
  canWrite: boolean;
  usedWebhookIds: Set<string>;
  dialogsByWebhookId: Map<string, ConnectedDialog[]>;
}

const URL_VISIBLE_TAIL_CHARS = 12;

const MiddleTruncatedUrl = ({ url = '' }: { url?: string }) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [url]);

  return (
    <InfoTooltip content={isTruncated ? url : undefined} size="large">
      <span className="flex min-w-0 text-foreground">
        <span ref={ref} className="truncate">
          {url.slice(0, -URL_VISIBLE_TAIL_CHARS)}
        </span>
        <span className="shrink-0">{url.slice(-URL_VISIBLE_TAIL_CHARS)}</span>
      </span>
    </InfoTooltip>
  );
};

export const createColumns = ({
  brainId,
  slug,
  canWrite,
  usedWebhookIds,
  dialogsByWebhookId,
}: ColumnsProps): ColumnDef<Webhook>[] => [
  {
    id: 'status',
    header: '',
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM', className: 'pl-4' },
    cell: ({ row }) => (
      <WebhookStatusDot
        webhook={row.original}
        isUsedInDialog={usedWebhookIds.has(row.original.webhook_id)}
      />
    ),
  },
  {
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <TruncatedText text={row.original.name} className="min-w-0 font-medium" />
        <RowActionIndicator action="view" />
      </div>
    ),
    enableColumnFilter: true,
    ...COLUMN_PRESETS.FLEX,
    minSize: COLUMN_PRESETS.MD.minSize,
    meta: { preset: 'FLEX' },
  },
  {
    accessorKey: 'type',
    header: t('webhook.config.type'),
    enableSorting: false,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    cell: ({ row }) => <WebhookTypeBadge webhook={row.original} />,
  },
  {
    id: 'assigned_to',
    header: t('webhook.assigned_to'),
    enableSorting: false,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    cell: ({ row }) => (
      <AssignedToCell
        brainId={brainId}
        slug={slug}
        dialogs={dialogsByWebhookId.get(row.original.webhook_id) ?? []}
      />
    ),
  },
  {
    accessorKey: 'url',
    header: t('webhook.config.url'),
    enableSorting: false,
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG' },
    cell: ({ row }) => <MiddleTruncatedUrl url={row.original.url} />,
  },
  {
    accessorKey: 'updated',
    header: ({ column }) => <SortableHeader column={column}>{t('common.updated')}</SortableHeader>,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    cell: ({ row }) => {
      const updated = row.original.updated;
      return updated ? (
        <span className="text-foreground">{timeFromNow(updated)}</span>
      ) : (
        <span className="text-foreground">-</span>
      );
    },
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
    cell: ({ row }) => <ActionsCell webhook={row.original} brainId={brainId} canWrite={canWrite} />,
  },
];
